import { invokeApig } from '../../libs/awsLib';
import { convertDateToUnixTime } from '../../libs/jsonToCSTDate';

const getAdmissionData = async function (entityId, patientId, admissionId) {
  const types = 'patient-demographics,patient';
  return await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/bulk?types=${types}`,
  });
};

const getEventAdmissionData = async function (entityId, admissionId) {
  const types = 'patient-demographics,patient,care-levels';
  return await invokeApig({
    path: `/entity/${entityId}/admission/${admissionId}/event-bulk?types=${types}`,
  });
};

const getAdmissionDataXSOLIS = async function (
  entityId,
  patientId,
  admissionId
) {
  const types = 'patient-demographics,patient,billable-potential,reviews';
  return await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/bulk?types=${types}`,
  });
};

const getCortexReviews = async function (entityId, patientId, admissionId) {
  const types = 'reviews';
  return await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/bulk?types=${types}`,
  });
};

const getEventAdmissionDataXSOLIS = async function (entityId, admissionId) {
  const types =
    'patient-demographics,patient,billable-potential,reviews,care-levels,review-comments';
  return await invokeApig({
    path: `/entity/${entityId}/admission/${admissionId}/event-bulk?types=${types}`,
  });
};

const getReviewComments = async function (entityId, admissionId, types) {
  if (types) {
    return await invokeApig({
      path: `/entity/${entityId}/admission/${admissionId}/event-bulk?types=${types}`,
    });
  } else {
    return await invokeApig({
      path: `/entity/${entityId}/admission/${admissionId}/event-bulk?types=review-comments`,
    });
  }
};

const getVitals = async function (entityId, patientId, admissionId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/vitals`,
  });
  return data.vitals;
};

const getVitalsRR = async function (entityId, admissionId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/admission/${admissionId}/vitals-rr`,
  });
  return data;
};

const getEncounterVitalTypes = async function (entityId, admissionId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/admission/${admissionId}/vitaltypes`,
  });
  return data;
};

const getLabs = async function (entityId, patientId, admissionId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/labs`,
  });
  return data.labs;
};

const getEncounterLabTypes = async function (entityId, admissionId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/admission/${admissionId}/labtypes`,
  });
  return data.types;
};

const getOrdersRR = async function (entityId, admissionId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/patient/0/admission/${admissionId}/orders`,
  });
  return data.orders;
};

const getLabTypesRR = async function () {
  const cacheKey = 'lab-types-rr';
  if (sessionStorage[cacheKey]) {
    return JSON.parse(sessionStorage[cacheKey]);
  } else {
    const data = await invokeApig({ path: `/types/lab-types-rr` });
    sessionStorage.setItem(cacheKey, JSON.stringify(data.types));
    return data.types;
  }
};

const getHistoricalLabs = async function (entityId, patientId, admissionId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/historical-labs`,
  });
  return data.historical_labs;
};

const getHistoricLabsRR = async function (admissionId, requestObject) {
  try {
    return await invokeApig({
      path: `/admission/${admissionId}/historical-labs-rr`,
      method: 'POST',
      body: requestObject,
    });
  } catch (e) {
    console.log(e);
  }
};

const getMeds = async function ({
  entityId,
  patientId,
  admissionId,
  showAllMedications,
}) {
  const data = await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/meds?showAllMedications=${showAllMedications}`,
  });
  return data.meds;
};

const getOrders = async function (entityId, patientId, admissionId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/orders`,
  });
  return data.orders;
};

const getDocuments = async function (entityId, patientId, admissionId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/documents`,
  });
  return data.documents;
};

const getAdmissionStatuses = async function (entityId, patientId, admissionId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/admission-statuses`,
  });
  return data.statuses;
};

const getDocument = async function (
  entityId,
  patientId,
  admissionId,
  documentId
) {
  const data = await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/documents/${documentId}`,
  });
  return data;
};

const getPdfDocumentRR = async function (entityId, admissionId, documentId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/admission/${admissionId}/document/${documentId}`,
  });
  return data;
};

const getPDFDocument = async function (
  entityId,
  patientId,
  admissionId,
  documentId
) {
  const data = await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/documents/${documentId}/s3`,
  });
  return data;
};

const getTxtDocumentRR = async function (entityId, admissionId, documentId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/admission/${admissionId}/documents/${documentId}/txt`,
  });
  return data;
};

const getXhtmlDocumentRR = async function (entityId, admissionId, documentId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/admission/${admissionId}/documents/${documentId}/xhtml`,
  });
  return data;
};

const getAcuity = async function (entityId, patientId, admissionId) {
  const allAcuity = await Promise.all([
    getTrendingAcuity(entityId, patientId, admissionId),
    getTrendingAcuityDetail(entityId, patientId, admissionId, 0),
  ]);

  const graphData = allAcuity[0].map((a) => ({
    date: convertDateToUnixTime(a.period_date),
    period_id: a.period_id,
    value: a.gradient,
    status_change_yn: a.status_change_yn,
    status_color_code: a.status_color_code,
    status_type: a.status_type,
    details: [],
  }));

  const details = allAcuity[1];
  graphData.forEach(function (period, index, graphArray) {
    var found = details.filter((detail) => {
      return detail.period === period.period_id;
    });
    if (found.length > 0) {
      graphArray[index].details.push(found);
    }
  });

  return graphData;
};

const getTrendingAcuity = async function (entityId, patientId, admissionId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/trending-acuity`,
  });
  return data.scores;
};

const getTrendingAcuityDetail = async function (
  entityId,
  patientId,
  admissionId,
  periodId
) {
  const data = await invokeApig({
    path: `/entity/${entityId}/patient/${patientId}/admission/${admissionId}/trending-acuity-details/${periodId}`,
  });
  return data.details;
};

const getWellnessRanges = async function () {
  const cacheKey = 'wellness-acuity-ranges';
  if (sessionStorage[cacheKey]) {
    return JSON.parse(sessionStorage[cacheKey]);
  } else {
    const data = await invokeApig({ path: `/wellness-acuity-ranges` });
    sessionStorage.setItem(cacheKey, JSON.stringify(data.ranges));
    return data.ranges;
  }
};

const getWellnessScores = async function (admissionId) {
  const data = await invokeApig({
    path: `/admission/${admissionId}/wellness-acuity-scores`,
  });
  return data;
};

const getWellnessFactors = async function (admissionId) {
  const data = await invokeApig({
    path: `/admission/${admissionId}/wellness-factors`,
  });
  return data;
};

const getTrendingAcuityRR = async function (admissionId) {
  const data = await invokeApig({ path: `/admission/${admissionId}/acuity` });
  return data;
};

const getTrendingAcuityDetailsRR = async function (admissionId) {
  const data = await invokeApig({
    path: `/admission/${admissionId}/trending-acuity-details`,
  });
  return data;
};

const getSLADueDate = async function (entityId, admissionId) {
  const data = await invokeApig({
    path: `/entity/${entityId}/admission/${admissionId}/sla-due-date-cortex`,
  });
  return data;
};

export {
  getAcuity,
  getAdmissionData,
  getAdmissionDataXSOLIS,
  getAdmissionStatuses,
  getCortexReviews,
  getDocument,
  getDocuments,
  getEncounterLabTypes,
  getEncounterVitalTypes,
  getEventAdmissionData,
  getEventAdmissionDataXSOLIS,
  getHistoricalLabs,
  getHistoricLabsRR,
  getLabs,
  getLabTypesRR,
  getMeds,
  getOrders,
  getOrdersRR,
  getPDFDocument,
  getPdfDocumentRR,
  getReviewComments,
  getSLADueDate,
  getTrendingAcuityDetailsRR,
  getTrendingAcuityRR,
  getTxtDocumentRR,
  getVitals,
  getVitalsRR,
  getWellnessFactors,
  getWellnessRanges,
  getWellnessScores,
  getXhtmlDocumentRR,
};
